/**
 * As ações sobre uma conta (docs/spec/admin.md), com a pergunta de cada uma: o motivo da
 * suspensão, a senha provisória, o nome digitado para apagar. O menu da lista e o painel de
 * detalhes usam as mesmas.
 */
import { useCallback } from 'react';
import { api } from '../api.js';
import { useT } from '../i18n/index.js';
import { ADMIN_LIMITS, type AdminLogAction } from '../shared.js';
import { describeError, useApp } from '../store.js';
import { askConfirm, askText } from '../ui/prompt.js';
import { toast } from '../ui/Toaster.js';
import { generatePassword } from './adminModel.js';
import { askPlan, type PlanDialogRow } from './AdminPlanDialog.js';

/** Faz a ação na conta. Devolve se ela aconteceu (cancelar a pergunta ou um erro do servidor: não). */
export function useAdminActions(): (row: PlanDialogRow, action: AdminLogAction) => Promise<boolean> {
  const t = useT();
  return useCallback(
    async (row, action) => {
      const token = useApp.getState().token;
      if (!token) return false;
      const { id, displayName: name } = row.user;
      try {
        if (action === 'suspend') {
          const reason = await askText({ title: t('admin.suspend.title', { name }), label: t('admin.suspend.label'), maxLength: ADMIN_LIMITS.reason, action: t('admin.action.suspend'), allowEmpty: true });
          if (reason === null) return false;
          await api.adminSuspend(token, id, reason);
          toast(t('admin.suspend.done', { name }), 'success');
        } else if (action === 'unsuspend') {
          if (!(await askConfirm({ title: t('admin.action.unsuspend'), message: t('admin.unsuspend.confirm', { name }), action: t('admin.action.unsuspend') }))) return false;
          await api.adminUnsuspend(token, id);
          toast(t('admin.unsuspend.done', { name }), 'success');
        } else if (action === 'password') {
          // a senha sugerida aparece no campo: o admin vê, troca se quiser e copia para passar à pessoa
          const password = await askText({ title: t('admin.password.title', { name }), label: t('admin.password.label'), initial: generatePassword(), maxLength: 200, action: t('admin.password.action') });
          if (password === null) return false;
          await api.adminSetPassword(token, id, password);
          let copied = true;
          try {
            await navigator.clipboard.writeText(password);
          } catch {
            copied = false;
          }
          toast(t(copied ? 'admin.password.doneCopied' : 'admin.password.done', { name }), 'success');
        } else if (action === 'plan') {
          // o diálogo manda o pedido e mostra o aviso; aqui só se sabe se mudou
          return await askPlan(row);
        } else if (action === 'promote' || action === 'demote') {
          const sure = await askConfirm({ title: t(`admin.action.${action}`), message: t(`admin.${action}.confirm`, { name }), action: t(`admin.action.${action}`), danger: action === 'demote' });
          if (!sure) return false;
          await api.adminSetRole(token, id, action === 'promote');
          toast(t(`admin.${action}.done`, { name }), 'success');
        } else {
          const typed = await askText({ title: t('admin.delete.title', { name }), label: t('admin.delete.label', { name }), maxLength: 40, action: t('admin.action.delete') });
          if (typed === null) return false;
          if (typed.toLocaleLowerCase() !== name.toLocaleLowerCase()) {
            toast(t('admin.delete.mismatch'), 'error');
            return false;
          }
          await api.adminDelete(token, id, typed);
          toast(t('admin.delete.done', { name }), 'success');
        }
        return true;
      } catch (error) {
        toast(describeError(error), 'error');
        return false;
      }
    },
    [t],
  );
}
